import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./SearchBar.module.css";

/**
 * Interactive component, A Search bar
 */

export function SearchBar({
  placeholder,
  onInput,
  searchDelay,
  minChar,
  value,
  onClick,
  onSubmit,
  ...props
}) {
  const ref = useRef(null);
  const [editValue, setEditValue] = useState("");
  const [searchDelayId, setSearchDelayId] = useState(null);

  useEffect(() => {
    if (typeof value === "string") {
      setEditValue(value);
    } else if (value == null) {
      setEditValue("");
    } else {
      throw new Error(
        `SearchInput value must be a string or undefined/null. Got: ${value} (${typeof value})`
      );
    }
  }, [value]);

  const _update = useCallback(
    (term) => {
      setEditValue(term);

      if (searchDelayId != null) {
        clearTimeout(searchDelayId);
      }

      if (minChar && term.length <= minChar) {
        return;
      }

      const id = setTimeout(() => {
        onInput?.(term);
      }, searchDelay * 300);

      setSearchDelayId(id);
    },
    [minChar, searchDelayId, searchDelay, onInput]
  );

  return (
    <div className={styles.searchContainer}>
      <input
        className={styles.searchBar}
        type="search"
        {...props}
        placeholder={placeholder}
        value={editValue}
        ref={ref}
        onChange={(e) => {
          _update(e.target.value);
          if (props.onChange) {
            props.onChange(e);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onInput?.(editValue);
            onSubmit?.(editValue);
          }
        }}
        aria-label="Search"
      />
      <div className={styles.cancelButton} onClick={onClick}>
        Cancel
      </div>
    </div>
  );
}

SearchBar.propTypes = {
  /**
   * Input placeholder text.
   */
  placeholder: PropTypes.string,
  /**
   * Function that fires on keystroke.
   */
  onInput: PropTypes.func,
  /**
   * An optional delay in ms before onInput is fired.
   */
  searchDelay: PropTypes.number,
  /**
   * Minimum characters that need to be exceeded before onInput is fired.
   * EG: If minChar is 2, the onInput event will not fire until the input value is greater than 2.
   */
  minChar: PropTypes.number,
  /**
   * Optional callback invoked when user submits with Enter.
   */
  onSubmit: PropTypes.func,
};

SearchBar.defaultProps = {
  onInput: () => {},
  searchDelay: 1,
  minChar: 0,
};
